import { ProfileTypes, TargetViewers } from '@/constants/profileType.constant';
import { Popover, Transition } from '@headlessui/react';
import Typography from '@ui/Typography';
import { cx } from 'cva';
import { last } from 'lodash-es';
import React, { Fragment, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { BiCaretDown, BiWorld } from 'react-icons/bi';
import { GoHomeFill } from 'react-icons/go';
import { MdVpnLock } from 'react-icons/md';
import useStore from '../../stores/store';
import ProfileAvatar from '../ProfileSelector/ProfileAvatar';
import CompanySearchBox from '../next-page-components/CompanySearchBox';

function PostViewersSelector(props) {
  const { disabled = false, post = null, isP2PFundraiser = false } = props;
  const user = useStore((state) => state.user);
  const isNonProfitAdmin = user.isNonProfitAdmin;
  const selectedProfile = user.profile.selectedProfile;
  const displayName = user.profile.displayName;
  const userProfile = user.profile;

  const targetAudiences = [
    {
      value: 'all',
      title: 'Everyone',
      subtitle: 'All my supporters',
      icon: BiWorld,
    },
    {
      value: 'selected-org',
      title: 'Selected Communities',
      subtitle: 'Select communities to target',
      icon: MdVpnLock,
    },
    {
      value: 'my-org',
      title: userProfile.activeCommunity.name,
      subtitle: 'Everyone in my community',
      icon: GoHomeFill,
    },
  ];

  const {
    getValues,
    setValue,
    formState: { errors },
    trigger,
    control,
  } = useFormContext();

  const getDefaultAudience = () => {
    const selected = getValues('targetViewers');
    let audience = targetAudiences.find((a) => a.value === selected);

    // TODO: keep the target audience same while editing
    if (disabled && post) {
      audience = targetAudiences.find((a) => a.value === post?.targetViewers);
    }

    if (audience) {
      return audience;
    }

    if (isP2PFundraiser) {
      setValue('targetViewers', TargetViewers.All);
      return targetAudiences[0];
    }

    return isNonProfitAdmin ? targetAudiences[0] : last(targetAudiences);
  };

  const [selectedAudience, setSelectedAudience] = useState(
    getDefaultAudience(),
  );

  const clearSelectedCompanies = () => {
    setValue('targetOrgs', [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger('targetOrgs');
  };

  useEffect(() => {
    const { postAs, targetViewers } = getValues();

    if (!post && postAs !== selectedProfile) {
      setValue('postAs', selectedProfile, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (!post && targetViewers !== selectedAudience.value) {
      setValue('targetViewers', selectedAudience.value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (isP2PFundraiser) {
      setValue('postAs', ProfileTypes.Individual);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAudience = (audience) => {
    if (isNonProfitAdmin) {
      setSelectedAudience(audience);
      setValue('targetViewers', audience.value, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (audience.value !== TargetViewers.SelectedOrg) {
        clearSelectedCompanies();
      }
    }
  };

  const handleTargetOrgSelection = (selected) => {
    setValue(
      'targetOrgs',
      selected.map((s) => s.id),
    );
    trigger('targetOrgs');
  };

  return (
    <div className="flex flex-col items-start justify-start gap-2 sm:flex-row sm:items-center sm:gap-6">
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              className={cx(
                'rounded-md px-1 py-2 pl-0',
                open && 'opacity-80',
                isNonProfitAdmin && 'group',
              )}
              disabled={disabled || isP2PFundraiser || !isNonProfitAdmin}>
              <div className="flex items-center gap-2">
                <ProfileAvatar />
                <div className="flex flex-col items-start justify-start">
                  <Typography
                    variant="subtitle1"
                    className={cx(
                      'group-hover:text-text-hyperlink',
                      open && 'text-text-hyperlink',
                    )}>
                    {displayName}
                  </Typography>
                  <div className="flex items-center justify-start gap-0.5">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={cx(
                        'text-left group-hover:text-text-hyperlink',
                        open && 'text-text-hyperlink',
                      )}>
                      Post to {selectedAudience.title}
                    </Typography>
                    {!disabled && (
                      <BiCaretDown className="text-text-secondary" />
                    )}
                  </div>
                </div>
              </div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-0 z-10 mt-1 transform px-4 sm:px-0">
                {({ close }) => (
                  <div className="w-max overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="relative grid gap-1 bg-white">
                      {targetAudiences.map((item) => (
                        <div
                          key={item.value}
                          role="button"
                          onClick={() => {
                            handleSelectAudience(item);
                            close();
                          }}
                          className="flex items-center px-3 py-2 transition duration-150 ease-in-out hover:bg-primary-light-1 focus:outline-none focus-visible:ring focus-visible:ring-secondary focus-visible:ring-opacity-50">
                          <div className="flex shrink-0 items-center justify-center text-white">
                            <item.icon
                              aria-hidden="true"
                              className="h-8 w-8 text-secondary opacity-80"
                            />
                          </div>
                          <div className="ml-3">
                            <Typography variant="h5">{item.title}</Typography>
                            <Typography variant="body1" color="textSecondary">
                              {item.subtitle}
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>

      {selectedAudience?.value === 'selected-org' && (
        <div className="flex flex-1 items-center gap-2">
          <selectedAudience.icon className="h-8 w-8 text-text-disabled" />
          <Controller
            control={control}
            name="targetOrgs"
            render={({ field: { value } }) => (
              <CompanySearchBox
                multiple
                initialValue={value}
                onChange={(val) => {
                  handleTargetOrgSelection(val);
                }}
                error={errors.targetOrgs?.message}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}

export default PostViewersSelector;
